import React, { useState } from 'react';
import LaunchModal from '../components/LaunchModal';

const SupportPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <div className="bg-gray-950 text-white antialiased">
      {/* Hero Section */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            We're Here to 
            <span className="brand-text-gradient"> Help You Succeed</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            Get the support you need to maximize your BarnBills experience. From quick questions 
            to custom plugin development, our team of horse industry experts is ready to help.
          </p>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>24-hour response time</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>English & Spanish support</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
              </svg>
              <span>Built by horse people</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Support Channels */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Get Support Your Way</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Choose the support channel that works best for you. Our team responds quickly 
              and speaks your language - both technically and equestrian.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Email Support */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Email Support</h3>
              <p className="text-gray-400 mb-6">
                Send us detailed questions and we'll respond within 24 hours with comprehensive answers.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-gray-300">
                  <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>24-hour response time</span>
                </div>
                <div className="flex items-center text-gray-300">
                  <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Screenshots & attachments</span>
                </div>
              </div>
              <button className="brand-gradient text-white px-6 py-3 rounded-lg font-semibold w-full transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25">
                Send Email
              </button>
            </div>

            {/* Live Chat */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Live Chat</h3>
              <p className="text-gray-400 mb-6">
                Get instant answers during business hours. Perfect for quick questions and real-time troubleshooting.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-gray-300">
                  <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Business hours support</span>
                </div>
                <div className="flex items-center text-gray-300">
                  <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Instant responses</span>
                </div>
              </div>
              <button className="border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 px-6 py-3 rounded-lg font-semibold w-full transition-all duration-200">
                Start Chat
              </button>
            </div>

            {/* Phone Support */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4">Phone Support</h3>
              <p className="text-gray-400 mb-6">
                For urgent issues or complex setups, speak directly with our technical team for immediate assistance.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center text-gray-300">
                  <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Urgent issues priority</span>
                </div>
                <div className="flex items-center text-gray-300">
                  <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Screen sharing available</span>
                </div>
              </div>
              <button className="border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 px-6 py-3 rounded-lg font-semibold w-full transition-all duration-200">
                Schedule Call
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Documentation Section */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Documentation & Resources</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Comprehensive guides and resources to help you get the most out of BarnBills. 
              More content being added regularly.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Quick Start Guide */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">Quick Start Guide</h3>
              <p className="text-gray-400 mb-4">Get up and running in 5 minutes with our step-by-step setup guide.</p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Documentation Coming Soon</p>
              </div>
            </div>

            {/* Video Tutorials */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.01M15 10h1.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">Video Tutorials</h3>
              <p className="text-gray-400 mb-4">Watch comprehensive video guides covering all major features and workflows.</p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Documentation Coming Soon</p>
              </div>
            </div>

            {/* Feature Guides */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">Feature Guides</h3>
              <p className="text-gray-400 mb-4">Detailed explanations of each feature with real-world use cases and best practices.</p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Documentation Coming Soon</p>
              </div>
            </div>

            {/* FAQ */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">FAQ</h3>
              <p className="text-gray-400 mb-4">Answers to the most common questions about BarnBills features and billing.</p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Documentation Coming Soon</p>
              </div>
            </div>

            {/* API Documentation */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">Troubleshooting Guide</h3>
              <p className="text-gray-400 mb-4">Step-by-step solutions for common issues, error messages, and technical problems.</p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Documentation Coming Soon</p>
              </div>
            </div>

            {/* Best Practices */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-4">Best Practices</h3>
              <p className="text-gray-400 mb-4">Learn from successful horse operations and optimize your farm management workflows.</p>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 text-center">
                <p className="text-orange-400 font-medium">Documentation Coming Soon</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training & Onboarding */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">Personalized Training & Onboarding</h2>
              <p className="text-gray-400 mb-8">
                Get your team up to speed quickly with personalized training sessions. Our experts 
                will help you configure BarnBills specifically for your operation's needs.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 brand-gradient rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Custom Setup Assistance</h3>
                    <p className="text-gray-400">We'll help configure BarnBills for your specific operation type and workflows.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 brand-gradient rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Team Training Sessions</h3>
                    <p className="text-gray-400">Live training sessions for your entire team, customized to your farm's processes.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 brand-gradient rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Data Migration Help</h3>
                    <p className="text-gray-400">Assistance migrating your existing horse and client data into BarnBills.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Content */}
            <div className="bg-gray-800 rounded-xl p-8 border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-6">Schedule Your Onboarding</h3>
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between py-3 border-b border-gray-700">
                  <span className="text-gray-300">Initial Setup Call</span>
                  <span className="text-green-400">✓ Included</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-700">
                  <span className="text-gray-300">Data Migration Assistance</span>
                  <span className="text-green-400">✓ Included</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-700">
                  <span className="text-gray-300">Team Training Session</span>
                  <span className="text-green-400">✓ Included</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-gray-300">Follow-up Support</span>
                  <span className="text-green-400">✓ Included</span>
                </div>
              </div>
              <button className="brand-gradient text-white px-6 py-3 rounded-lg font-semibold w-full transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25">
                Schedule Onboarding
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Community & Status */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-1 gap-12 justify-center">
            {/* System Status */}
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 max-w-lg mx-auto">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-white">System Status</h3>
              </div>
              <p className="text-gray-400 mb-6">
                Monitor BarnBills system health and receive updates about maintenance or outages. 
                We maintain 99.9% uptime.
              </p>
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-300">API Status</span>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-400 rounded-full mr-2"></div>
                    <span className="text-green-400">Operational</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-300">Payment Processing</span>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-400 rounded-full mr-2"></div>
                    <span className="text-green-400">Operational</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-300">Mobile App</span>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-400 rounded-full mr-2"></div>
                    <span className="text-green-400">Operational</span>
                  </div>
                </div>
              </div>
              <button className="border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 px-6 py-3 rounded-lg font-semibold w-full transition-all duration-200">
                View Status Page
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-6">
            Still Need Help?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Our support team is standing by to help you succeed. Whether you need technical assistance 
            or want to discuss custom plugin development, we're here for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="brand-gradient text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25">
              Contact Support Team
            </button>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200"
            >
              Get Notified at Launch
            </button>
          </div>
        </div>
      </section>

      <LaunchModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default SupportPage; 